import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import {
	AGENT_ACCENTS,
	AGENT_ICONS,
	type AgentKey,
} from "@/components/brand/ai-agent-icons";
import { LogoIcon } from "@/components/brand/logo-icon";

export function MarketingHero({
	eyebrow,
	agent,
	title,
	description,
	primaryCta = { label: "Start now", to: "/choose-plan" },
	secondaryCta = { label: "See pricing", to: "/pricing" },
	children,
}: {
	eyebrow?: string;
	agent?: AgentKey;
	title: ReactNode;
	description: ReactNode;
	primaryCta?: { label: string; to: string };
	secondaryCta?: { label: string; to: string } | null;
	children?: ReactNode;
}) {
	return (
		<section className="rounded-md bg-white dark:bg-black px-6 py-12 text-center sm:px-10 sm:py-16">
			{agent ? (
				<div className="flex items-center justify-center gap-3">
					<span
						className={`flex size-10 items-center justify-center rounded-full ${AGENT_ACCENTS[agent].bg}`}
					>
						{AGENT_ICONS[agent](`size-7 ${AGENT_ACCENTS[agent].text}`)}
					</span>
					<span className="text-base font-light text-muted-foreground/70">
						+
					</span>
					<span className="flex size-10 items-center justify-center rounded-full bg-primary">
						<LogoIcon bare className="size-7 text-primary-foreground" />
					</span>
				</div>
			) : (
				eyebrow && (
					<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
						{eyebrow}
					</p>
				)
			)}
			<h1 className="mx-auto mt-5 max-w-3xl text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
				{title}
			</h1>
			<p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
				{description}
			</p>
			<div className="mt-6 flex flex-wrap items-center justify-center gap-3">
				<Link
					to={primaryCta.to}
					className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
				>
					{primaryCta.label}
					<ArrowRight className="size-4" />
				</Link>
				{secondaryCta && (
					<Link
						to={secondaryCta.to}
						className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
					>
						{secondaryCta.label}
					</Link>
				)}
			</div>
			{children}
		</section>
	);
}
